import _defineProperty from "@babel/runtime/helpers/defineProperty";
import _toConsumableArray from "@babel/runtime/helpers/toConsumableArray";

function ownKeys(object, enumerableOnly) { var keys = Object.keys(object); if (Object.getOwnPropertySymbols) { var symbols = Object.getOwnPropertySymbols(object); enumerableOnly && (symbols = symbols.filter(function (sym) { return Object.getOwnPropertyDescriptor(object, sym).enumerable; })), keys.push.apply(keys, symbols); } return keys; }

function _objectSpread(target) { for (var i = 1; i < arguments.length; i++) { var source = null != arguments[i] ? arguments[i] : {}; i % 2 ? ownKeys(Object(source), !0).forEach(function (key) { _defineProperty(target, key, source[key]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(target, Object.getOwnPropertyDescriptors(source)) : ownKeys(Object(source)).forEach(function (key) { Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key)); }); } return target; }

import _regeneratorRuntime from "@babel/runtime/regenerator";
import { getDefaultMiddleware } from '@reduxjs/toolkit';
import { createStore } from 'redux-dynamic-modules';
import createSagaMiddleware, { END } from 'redux-saga';
import { all } from 'redux-saga/effects';
import bentoApiReduxMiddleware from './bentoApiReduxMiddleware';
import createChannelMiddleware from './channelMiddleware';
import createCookieInstanceWithDefaultOptions from './cookieInstance';
import logger from './logger';
var debug = logger.extend('configureStore');
export function createRootSaga(sagas) {
  return /*#__PURE__*/_regeneratorRuntime.mark(function rootSaga() {
    return _regeneratorRuntime.wrap(function rootSaga$(_context) {
      while (1) {
        switch (_context.prev = _context.next) {
          case 0:
            debug('Running %d saga(s).', sagas.length);
            _context.next = 3;
            return all(sagas.map(function (saga) {
              return saga();
            }));

          case 3:
            debug('Ending root saga.');

          case 4:
          case "end":
            return _context.stop();
        }
      }
    }, rootSaga);
  });
}
export function getDefaultExtensions() {
  var context = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : {};
  var sagaMiddleware = createSagaMiddleware();
  context.sagaMiddleware = sagaMiddleware;
  var middleware = [createChannelMiddleware()].concat(_toConsumableArray(getDefaultMiddleware({
    thunk: {
      extraArgument: context
    },
    serializableCheck: false,
    immutableCheck: process.env.NODE_ENV === 'test'
  })), [sagaMiddleware, bentoApiReduxMiddleware(context)]);

  if (process.env.NODE_ENV !== 'production') {
    // Logger middleware: print actions dispatched through the store for
    // easier debugging.
    var _require = require('./loggerMiddleware'),
        createLoggerMiddleware = _require["default"];

    middleware.push(createLoggerMiddleware());
  }

  return [{
    middleware: middleware
  }];
}
export function getInitialMiddleware() {
  return getDefaultExtensions()[0].middleware;
}
/**
 * Create and return a Redux store instance. This function's signature is
 * designed to work with `next-redux-wrapper`.
 */

export default function configureStore(preloadedState, options) {
  // There will either be `options.req` (during SSR getInitialProps) or
  // `preloadedState` (SSR render phase, and client-side hydration), but never
  // both.
  var parentCookieDomain = options.req ? options.req.context.domain.parentCookieDomain : preloadedState.domain.parentCookieDomain;

  var context = _objectSpread(_objectSpread({}, options), {}, {
    hasPreloadedState: preloadedState != null,
    // If we have `options.req` then use that existing Cookies instance.
    // However, if `anonymousServerSession` is enabled, we want the React
    // components to ignore the user's real cookies, since the output would be
    // specific to that user, so create an empty Cookies instance instead.
    // In the browser, the new Cookies instance will automatically read the
    // cookies that the browser has access to.
    cookies: options.req && !options.req.context.anonymousServerSession ? options.req.universalCookies : createCookieInstanceWithDefaultOptions({
      domain: parentCookieDomain,
      path: '/'
    })
  });

  var registry = options.registry,
      _options$enhancers = options.enhancers,
      enhancers = _options$enhancers === void 0 ? [] : _options$enhancers,
      _options$initialModul = options.initialModules,
      initialModules = _options$initialModul === void 0 ? [] : _options$initialModul; // `@techstyle/next-server` extensions can add "modules" and "extensions"
  // (see: `redux-dynamic-modules`) and "sagas" (see: `redux-saga`).

  var reduxModules = _toConsumableArray(initialModules);

  var reduxExtensions = [];
  var reduxSagas = [];

  if (registry) {
    registry.forEach(function (middleware) {
      var redux = middleware.redux;

      if (typeof redux === 'function') {
        redux = redux(context);
      }

      if (redux) {
        if (redux.modules) {
          reduxModules.push.apply(reduxModules, _toConsumableArray(redux.modules));
          redux.modules.forEach(function (module) {
            if (module.sagas) {
              reduxSagas.push.apply(reduxSagas, _toConsumableArray(module.sagas));
            }
          });
        }

        if (redux.extensions) {
          reduxExtensions.push.apply(reduxExtensions, _toConsumableArray(redux.extensions));
        }

        if (redux.sagas) {
          reduxSagas.push.apply(reduxSagas, _toConsumableArray(redux.sagas));
        }
      }
    });
  }

  var store = createStore.apply(void 0, [{
    initialState: preloadedState,
    extensions: reduxExtensions,
    enhancers: enhancers
  }].concat(_toConsumableArray(reduxModules)));

  if (context.sagaMiddleware) {
    if (!context.isServer || !context.hasPreloadedState) {
      // Run the root saga only if we're running in the browser or in the
      // `getInitialProps` phase on the server. (When the store gets recreated
      // during the server's render phase, there should be no more actions being
      // dispatched and thus no need for sagas.)
      var rootSaga = createRootSaga(reduxSagas);
      store.rootSagaTask = context.sagaMiddleware.run(rootSaga);

      store.endRootSaga = function () {
        store.dispatch(END);
        return store.rootSagaTask.toPromise();
      };
    }
  }

  return store;
}